import React from "react";
import { Button } from "@/components/ui/button";
import { Pencil, Trash, ChartBar, List as DragHandle } from "@phosphor-icons/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Formatter EUR shared con ServicesAdmin. */
const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

/**
 * Iter208.24 — Riga sortable con handle di drag.
 */
function SortableRow({ s, onEdit, onDelete, onStats }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: s._id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? "hsl(var(--surface-alt))" : undefined,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="border-b hover:bg-[color:hsl(var(--surface-alt))]"
      data-testid={`svc-row-${s._id}`}
    >
      <td className="w-8 py-3 pl-2 pr-1 text-center cursor-grab active:cursor-grabbing select-none">
        <span
          {...attributes}
          {...listeners}
          className="inline-flex text-slate-400 hover:text-slate-700"
          title="Trascina per riordinare"
          data-testid={`svc-drag-${s._id}`}
          aria-label={`Trascina ${s.name}`}
        >
          <DragHandle size={16} weight="bold" />
        </span>
      </td>
      <td data-label="Nome" className="py-3 font-medium">
        {s.name}
        {s.has_flexible_price && (
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[9px] font-semibold uppercase tracking-wider">
            💰 Flex
          </span>
        )}
      </td>
      <td data-label="Categoria">{s.category}</td>
      <td data-label="Prezzo" className="font-mono">{fmt(s.base_price)}</td>
      <td data-label="Step Flusso" className="text-muted-foreground">
        {s.workflow_id ? "workflow collegato" : "—"}
      </td>
      <td data-label="Consegna">{s.delivery_time_days}gg</td>
      <td data-label="Attivo">{s.is_active ? "✓" : "—"}</td>
      <td className="text-right mobile-actions">
        {s.has_flexible_price && onStats && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onStats(s)}
            data-testid={`stats-svc-${s._id}`}
            title="Statistiche risposte Prezzo Flessibile"
          >
            <ChartBar size={14} />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(s)}
          data-testid={`edit-svc-${s._id}`}
        >
          <Pencil size={14} />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(s._id)}>
          <Trash size={14} />
        </Button>
      </td>
    </tr>
  );
}

/**
 * Tabella dei servizi (admin). Con drag-and-drop per riordinare l'elenco.
 * Iter208.24 — Nuovo endpoint `PUT /services/reorder` persiste `sort_order`.
 *
 * Props:
 *  - services: array servizi
 *  - onEdit(service): callback modifica
 *  - onDelete(id): callback eliminazione
 *  - onStats(service): callback statistiche prezzo flessibile (Iter118)
 *  - onReorder(newList): opzionale — genitore aggiorna lo stato locale
 */
export default function ServiceList({ services, onEdit, onDelete, onStats, onReorder }) {
  const [items, setItems] = React.useState(services);

  // Sync se il parent ricarica la lista
  React.useEffect(() => {
    setItems(services);
  }, [services]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((x) => x._id === active.id);
    const newIndex = items.findIndex((x) => x._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    onReorder?.(next);
    try {
      await api.put("/services/reorder", { ids: next.map((x) => x._id) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio ordine servizi");
      // Rollback ordine locale in caso di errore
      setItems(items);
      onReorder?.(items);
    }
  };

  return (
    <div className="kpi-card">
      <table className="w-full text-sm mobile-cards" data-testid="services-admin-table">
        <thead>
          <tr className="text-left border-b">
            <th className="w-8"></th>
            <th className="py-2">Nome</th>
            <th>Categoria</th>
            <th>Prezzo</th>
            <th>Step Flusso</th>
            <th>Consegna</th>
            <th>Attivo</th>
            <th></th>
          </tr>
        </thead>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((x) => x._id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {items.map((s) => (
                <SortableRow
                  key={s._id}
                  s={s}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStats={onStats}
                />
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>
    </div>
  );
}
